/**
 * D5 -- keep a popover on screen.
 *
 * The bell's panel is anchored to the bell and 320px wide, which is
 * fine until the bell is near an edge: the walkthrough found it drawn
 * half off the left of the window, so half the notifications could not
 * be read at all. Anchoring is a layout rule; staying inside the window
 * is a measurement, and only the second can be right at every width and
 * in both directions.
 *
 * Pure so it can be tested: jsdom reports every rect as zero, so a test
 * driving the real element would pass whatever this did.
 */
export interface ClampInput {
  /** Where the panel currently starts, in viewport coordinates. */
  left: number;
  width: number;
  viewportWidth: number;
  /** Breathing room to leave at whichever edge it would have crossed. */
  margin?: number;
}

/**
 * How far to move the panel horizontally, in pixels. 0 when it already
 * fits: a popover that shifts when it did not need to reads as drift.
 */
export function clampOffset({
  left,
  width,
  viewportWidth,
  margin = 8,
}: ClampInput): number {
  // A panel wider than the window cannot be made to fit; pin its start
  // edge, which keeps the readable half readable.
  if (width + margin * 2 >= viewportWidth) return margin - left;
  if (left < margin) return margin - left;
  const right = left + width;
  if (right > viewportWidth - margin) return viewportWidth - margin - right;
  return 0;
}

/**
 * C4 (round 8) -- hang the panel off the side the trigger is actually
 * on, in either direction. Fourth round, same pixel coordinates.
 *
 * The panel carries `absolute end-0`, which is the right logical rule
 * for a bell at the END of the bar: in English the bell is on the right
 * and the panel's right edge aligns with it. In Arabic `end-0` resolves
 * to `left: 0` -- but the bell does NOT move to the left of the header,
 * so the panel was pinned to the bell's LEFT edge and grew rightward
 * off the window. The viewport clamp then hauled it back, which is why
 * it measured as correctly inside the window and 165px adrift from the
 * bell that opened it: the clamp was doing its job on top of an anchor
 * that was wrong.
 *
 * A physical `right-0` would fix Arabic by asserting the same Latin
 * assumption in the other direction. So: align by MEASUREMENT. A panel
 * hangs from whichever side its trigger is nearer, wherever the layout
 * puts it and whichever way the page reads, and the viewport clamp
 * stays a separate, later step.
 */
export interface AlignInput {
  /** The trigger's box, in viewport coordinates. */
  triggerLeft: number;
  triggerRight: number;
  panelWidth: number;
  viewportWidth: number;
  margin?: number;
}

/** Where the panel's left edge should sit, in viewport coordinates. */
export function alignedLeft({
  triggerLeft,
  triggerRight,
  panelWidth,
  viewportWidth,
  margin = 8,
}: AlignInput): number {
  const hangsFromRight = (triggerLeft + triggerRight) / 2 > viewportWidth / 2;
  const desired = hangsFromRight ? triggerRight - panelWidth : triggerLeft;
  // Then, and only then, keep it on screen. Order matters: clamping
  // first and anchoring second is how the panel came to be both inside
  // the window and nowhere near its bell.
  return (
    desired + clampOffset({ left: desired, width: panelWidth, viewportWidth, margin })
  );
}
